import React from 'react';
// @ts-ignore
import { formatPriceNoCode, getCurrencySymbol } from 'invitemember-utils-public/currencies';
import resolveConfig from 'tailwindcss/resolveConfig';
import { v4 as uuidv4 } from 'uuid';

export const tailwindConfig = () => {
  // Tailwind config
  return resolveConfig('./src/css/tailwind.config.js')
}

export const hexToRGB = (h) => {
  let r = 0;
  let g = 0;
  let b = 0;
  if (h.length === 4) {
    r = `0x${h[1]}${h[1]}`;
    g = `0x${h[2]}${h[2]}`;
    b = `0x${h[3]}${h[3]}`;
  } else if (h.length === 7) {
    r = `0x${h[1]}${h[2]}`;
    g = `0x${h[3]}${h[4]}`;
    b = `0x${h[5]}${h[6]}`;
  }
  return `${+r},${+g},${+b}`;
};

export const formatValue = (value) => Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumSignificantDigits: 3,
  notation: 'compact',
}).format(value);

export const formatPercentage = (value) => {
  return Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

export const formatThousands = (value) => Intl.NumberFormat('en-US', {
  maximumSignificantDigits: 3,
  notation: 'compact',
}).format(value);

export const priceView = (amount, currency = 'USD') => {
  if (currency === 'XTR') {
    return amount;
  } else {
    if (currency !== getCurrencySymbol(currency)) {
      const isNegative = amount < 0;
      const absoluteAmount = Math.abs(amount);

      return isNegative
        ? `-${getCurrencySymbol(currency)}${formatPriceNoCode(absoluteAmount, currency)}`
        : `${getCurrencySymbol(currency)}${formatPriceNoCode(absoluteAmount, currency)}`;
    }
    return amount;
  }
};

/**
 * Gets user data and returns fancy formatted user badges, can return single badge or multiple
 * @param  {Object.<{user: Object, project: (Object||undefined)}>} data data must have user object, project is optional
 * @param  {string} returnType type of data to be returned. `badge` - returns one styled badge,
 * `badges` - returns multiple styled badges,
 * `status` - returns one raw status
 * @return {Array.<React.ReactElement>} returns array of React elements with key attribute
 **/
export const getUserStatus = (data, returnType) => {
  const { user, project } = data;

  const badge = (color, label) => (
      <div className={`badge -${color}`}>{label}</div>
  );

  if (!user)
    return null;

  // TODO Check badges colors
  const statuses = [
    {
      condition: project && (project.creatorUserId === user.userId),
      label: 'Project owner',
      color: 'purple',
    },
    {
      condition: user.paymentsCount === 0 && user.subscriptionsCount === 0,
      label: 'Lead',
      color: 'gray',
    },
    {
      condition: user.activePaymentsCount > 0 || user.activeSubscriptionsCount > 0,
      label: 'Customer',
      color: 'blue',
    },
    {
      condition: user.activePaymentsCount === 0 && user.activeSubscriptionsCount === 0 && (user.paymentsCount + user.subscriptionsCount > 0),
      label: 'Churned',
      color: 'dark',
    },
    {
      condition: user.affiliateId,
      label: 'Affiliate partner',
      color: 'yellow',
    },
    {
      condition: user.isTeamMember,
      label: 'Team member',
      color: 'purple',
    },
    {
      condition: user.isBanned,
      label: 'Banned',
      color: 'red',
    },
    {
      condition: user.isWhitelisted,
      label: 'Whitelisted',
      color: 'yellow',
    },
  ];

  if (returnType === 'status') {
    for (const status of statuses) {
      if (status.condition) {
        return status.label;
      }
    }
    return null;
  }

  const result = statuses.reduce((acc, { condition, color, label }, index) => {
    if (condition) {
      const newBadge = badge(color, label);
      acc.push(React.cloneElement(newBadge, { key: `user-badge-${uuidv4()}` }));
    }
    return acc;
  }, []);
  if (returnType === 'badge') return result[0];

  return result;
};


/**
 * throttles given function once every `wait` milliseconds
 * @param {Function} func function to debounce
 * @param {number} wait number of milliseconds to wait
 * @returns {Function} debounced function.
 */
export const debounce = (func, wait) => {
  let timeout;

  return function (...args) {
    const context = this;

    const later = () => {
      timeout = null;
      func.apply(context, args);
    };

    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

export const sanitizeSeed = (str) => {
  return encodeURI(str.replace(/[^a-z0-9]/gi,''));
}

export async function requestCheckoutSession({
  endpoint,
  method = "POST",
  name,
  isYearly,
  region,
  updateBillingInfoMode = false,
  telegramUser,
}) {
  const {
    telegramUserId,
    firstName,
    lastName,
    username,
    photoUrl,
    authDate,
    hash,
    token,
  } = telegramUser;

  const planName = name.toLowerCase().includes("free") ? "free" : name.toLowerCase();
  const payload = {
    plan: planName,
    billed: isYearly ? "yearly" : "monthly",
    region: region ?? "CY",
    id: telegramUserId,
    first_name: firstName,
    auth_date: authDate,
    hash: hash,
    telegramBotUsername: process.env.REACT_APP_BOT_USERNAME,
    updateBillingInfoMode: !!updateBillingInfoMode,
  };

  if (lastName) payload.last_name = lastName;
  if (username) payload.username = username;
  if (photoUrl) payload.photo_url = photoUrl;

  return await fetch(endpoint, {
    method,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      accessToken: token,
      ...payload,
    }),
  });
}

export const isActiveSubscription = (subscription) => {
  return subscription &&
        Object.keys(subscription).length > 0 &&
        subscription.stripeSubscription &&
        ['active', 'trialing', 'past_due'].includes(subscription.stripeSubscription.status) &&
        !subscription.stripeSubscription.cancel_at_period_end;
};

export const isCanceledOrEndingSubscription = (subscription) => {
  return subscription &&
        subscription.stripeSubscription &&
        (subscription.stripeSubscription.status === "canceled" ||
        (subscription.stripeSubscription.cancel_at_period_end &&
        ['active', 'trialing', 'past_due'].includes(subscription.stripeSubscription.status)));
};

export const isCanceledSubscription = (subscription) => {
  return subscription &&
        subscription.stripeSubscription &&
        (subscription.stripeSubscription.status === "canceled");
};

export const isEndingSubscription = (subscription) => {
  return subscription &&
        subscription.stripeSubscription &&
        (subscription.stripeSubscription.cancel_at_period_end &&
        ['active', 'trialing', 'past_due'].includes(subscription.stripeSubscription.status));
};

/**
 * Remove specified URL parameters from current URL, DO NOT USE in react components
 * @param {string[]} paramsToRemove - Array of parameter names to remove
 */
export const removeUrlParams = (paramsToRemove) => {
	const currentUrl = new URL(window.location.href);
	const currentParams = currentUrl.searchParams;
	
	paramsToRemove.forEach(param => {
		currentParams.delete(param);
	});
	
	const newUrl = `${currentUrl.pathname}${currentParams.toString() ? `?${currentParams.toString()}` : ''}`;
	
	window.history.replaceState({}, '', newUrl);
    // window.dispatchEvent(new PopStateEvent('popstate', { state: {} }));
};

/**
 * Generate redirect URL based on search parameters after login/signup
 * @param {URLSearchParams} searchParams - Search parameters from URL
 * @returns {string} - Redirect URL
 */
export const getRedirectUrlWithParams = (searchParams) => {
	if (searchParams.has("projectId")) {
		const projectId = searchParams.get("projectId");
		
		// Check for specific target pages
		if (searchParams.has("userId")) {
			const userId = searchParams.get("userId");
			if (userId && userId.trim() !== '') {
				return `/user/${userId}?projectId=${encodeURIComponent(projectId || '')}`;
			} else {
				// Empty userId - redirect to users list
				return `/users?projectId=${encodeURIComponent(projectId || '')}`;
			}
		} else if (searchParams.has("subscriptionId")) {
			const subscriptionId = searchParams.get("subscriptionId");
			if (subscriptionId && subscriptionId.trim() !== '') {
				return `/sales/subscription/${subscriptionId}?projectId=${encodeURIComponent(projectId || '')}`;
			} else {
				// Empty subscriptionId - redirect to subscriptions list
				return `/sales/subscriptions?projectId=${encodeURIComponent(projectId || '')}`;
			}
		} else if (searchParams.has("paymentId")) {
			const paymentId = searchParams.get("paymentId");
			if (paymentId && paymentId.trim() !== '') {
				return `/sales/payments/${paymentId}?projectId=${encodeURIComponent(projectId || '')}`;
			} else {
				// Empty paymentId - redirect to payments list
				return `/sales/payments?projectId=${encodeURIComponent(projectId || '')}`;
			}
		} else {
			// Default to dashboard with projectId
			return `/?projectId=${encodeURIComponent(projectId || '')}`;
		}
	} else {
		return '/';
	}
};
